import { litDate } from './periodes';
import { sansPaiementsEtat } from './paiements-etat';

/**
 * Clôture d'exercice.
 *
 * La comptabilité ne se lit pas en continu : elle se lit par exercice, et
 * l'exercice écoulé se fige pour la déclaration d'impôts. Une fois clôturé, il
 * ne doit plus bouger — c'est pourquoi les chiffres sont recopiés dans la
 * clôture plutôt que recalculés à l'affichage. Une facture antidatée saisie
 * l'année suivante ne doit pas réécrire un bilan déjà remis au fisc.
 */

/** Clés des réglages de société où vivent ces données. */
export const CLE_CLOTURES = 'clotures';
export const CLE_COMPTABLE = 'comptable';
export const CLE_BILAN = 'bilan';

/**
 * Année comptable d'une pièce.
 *
 * Une facture peut être rattachée à un exercice autre que celui de sa date
 * d'émission — une prestation de décembre facturée en janvier. `dateCompta`
 * porte ce rattachement quand il diffère ; à défaut, la date de la pièce fait
 * foi.
 *
 * @param {object} piece facture ou dépense
 * @param {Date} [maintenant] date de référence, pour les tests
 * @returns {number} l'année de rattachement
 */
export function anneeDe(piece, maintenant = new Date()) {
  const d = litDate(piece?.dateCompta || piece?.date, maintenant);
  return d ? d.getFullYear() : maintenant.getFullYear();
}

/**
 * Années pour lesquelles il existe des écritures, la plus récente en tête.
 *
 * L'année courante y figure toujours : un exercice qui vient de commencer n'a
 * pas encore de pièce, et doit pourtant être consultable.
 */
export function anneesDisponibles(factures, frais, maintenant = new Date()) {
  const an = maintenant.getFullYear();
  const toutes = [
    ...(factures || []).map((f) => anneeDe(f, maintenant)),
    ...(frais || []).map((x) => anneeDe(x, maintenant)),
    an,
  ];
  return [...new Set(toutes)].sort((a, b) => b - a);
}

/**
 * La première année où le comptable compte : celle de la première dépense
 * notée (les paiements à l'État exceptés), ou l'année en cours.
 *
 * <p>Une année où l'entreprise n'a noté que des acomptes reçus — « pour 2025,
 * juste les deux acomptes déclarés » — ne porte pas ses honoraires : ils
 * baissaient le bénéfice 2025 de 2'000.– sans aucune pièce.
 *
 * @param {object[]} frais dépenses
 * @param {Date} [maintenant] date de référence
 * @returns {number} l'année
 */
export function premiereAnneeDuComptable(frais, maintenant = new Date()) {
  return Math.min(
    maintenant.getFullYear(),
    ...sansPaiementsEtat(frais)
      .map((x) => anneeDe(x, maintenant))
      .filter(Number.isFinite),
  );
}

/**
 * Fige les chiffres d'un exercice.
 *
 * Le bénéfice se calcule ici plutôt qu'à l'affichage : il doit rester celui
 * qui a été déclaré, même si une charge est ajoutée plus tard.
 *
 * @param {number} annee exercice clôturé
 * @param {object} chiffres totaux de l'exercice
 * @param {string} dateCloture jour de la clôture, écrit à la suisse
 * @returns {object} la clôture, prête à être conservée
 */
export function composeCloture(annee, chiffres, dateCloture) {
  const produits = (chiffres.ca || 0) + (chiffres.ventes || 0);
  const charges =
    (chiffres.achats || 0) +
    (chiffres.frais || 0) +
    (chiffres.vehicules || 0) +
    (chiffres.salaires || 0) +
    (chiffres.comptable || 0);
  return {
    annee,
    dateCloture,
    ca: chiffres.ca || 0,
    ventes: chiffres.ventes || 0,
    produits,
    achats: chiffres.achats || 0,
    frais: chiffres.frais || 0,
    vehicules: chiffres.vehicules || 0,
    salaires: chiffres.salaires || 0,
    comptable: chiffres.comptable || 0,
    charges,
    resultat: produits - charges,
    banque: chiffres.banque || 0,
    caisse: chiffres.caisse || 0,
    debiteurs: chiffres.debiteurs || 0,
    stock: chiffres.stock || 0,
    immobilisations: chiffres.immobilisations || 0,
    dettes: chiffres.dettes || 0,
    acomptesAvance: chiffres.acomptesAvance || 0,
    actif:
      (chiffres.banque || 0) +
      (chiffres.caisse || 0) +
      (chiffres.debiteurs || 0) +
      (chiffres.stock || 0) +
      (chiffres.immobilisations || 0),
    fondsPropres:
      (chiffres.banque || 0) +
      (chiffres.caisse || 0) +
      (chiffres.debiteurs || 0) +
      (chiffres.stock || 0) +
      (chiffres.immobilisations || 0) -
      (chiffres.dettes || 0) -
      (chiffres.acomptesAvance || 0),
  };
}

/**
 * Périodes de facturation du comptable, selon sa fréquence.
 *
 * @param {string} freq `mensuel`, `trimestriel`, `semestriel` ou `annuel`
 * @returns {Array<[string, string]>} clé et libellé de chaque période
 */
export function periodesComptable(freq) {
  if (freq === 'trimestriel') {
    return [
      ['T1', '1er trimestre'],
      ['T2', '2e trimestre'],
      ['T3', '3e trimestre'],
      ['T4', '4e trimestre'],
    ];
  }
  if (freq === 'semestriel') {
    return [
      ['S1', '1er semestre'],
      ['S2', '2e semestre'],
    ];
  }
  if (freq === 'annuel') {
    return [['AN', 'Année complète']];
  }
  return [
    ['01', 'Janvier'],
    ['02', 'Février'],
    ['03', 'Mars'],
    ['04', 'Avril'],
    ['05', 'Mai'],
    ['06', 'Juin'],
    ['07', 'Juillet'],
    ['08', 'Août'],
    ['09', 'Septembre'],
    ['10', 'Octobre'],
    ['11', 'Novembre'],
    ['12', 'Décembre'],
  ];
}

/**
 * Ce qui a été versé au comptable sur un exercice, et ce qui reste dû.
 *
 * @param {object} comptable fiche du comptable
 * @param {number} annee exercice considéré
 * @returns {{paye: number, du: number, reste: number, nb: number, total: number}}
 */
export function soldeComptable(comptable, annee) {
  const montant = Number(comptable?.montant) || 0;
  const periodes = periodesComptable(comptable?.freq);
  const regle = comptable?.paye || {};
  const nb = periodes.filter((p) => regle[annee + '-' + p[0]]).length;
  return {
    nb,
    total: periodes.length,
    paye: nb * montant,
    du: periodes.length * montant,
    reste: (periodes.length - nb) * montant,
  };
}

/**
 * Exercices entre lesquels un acompte peut être rattaché.
 *
 * Un acompte encaissé en décembre pour des travaux du printemps suivant pose
 * une question que l'application ne peut pas trancher seule : la méthode des
 * recettes le compte l'année de l'encaissement, la méthode d'engagement
 * l'année des travaux — il est alors un produit reçu d'avance. Les deux sont
 * admises en Suisse ; c'est à l'entreprise, avec sa fiduciaire, de choisir.
 *
 * @param {object} piece facture d'acompte
 * @param {Date} [maintenant] date de référence, pour les essais
 * @returns {{encaisse: number, travaux: number, retenu: number}} les deux
 *   exercices possibles et celui qui est retenu
 */
export function exercicesAcompte(piece, maintenant = new Date()) {
  const encaisse = anneeDe({ date: piece?.date }, maintenant);
  const retenu = anneeDe(piece, maintenant);
  return { encaisse, travaux: encaisse + 1, retenu };
}

/**
 * Date comptable à inscrire pour rattacher un acompte à un exercice.
 *
 * Rien à inscrire pour l'année de l'encaissement : la date de la pièce suffit,
 * et une seconde date qui répète la première finit par la contredire.
 *
 * @param {number} annee exercice choisi
 * @param {number} encaisse exercice de l'encaissement
 * @returns {string|null} la date comptable, ou `null`
 */
export function dateComptaPour(annee, encaisse) {
  return annee === encaisse ? null : '01.01.' + annee;
}
